import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Network, Database, Send, Target, Cpu, Briefcase, CheckCircle2 } from "lucide-react";

const SERVICES = [
  {
    icon: Network,
    title: "Affiliate and Performance Marketing",
    body: "We support customer acquisition through affiliate partnerships, commercial relationships, campaign management, partner networks and performance based channels. Our partner stack is curated, monitored and managed with clear performance expectations and traffic quality standards.",
    points: ["Partner sourcing and onboarding", "Commercial structures and tracking", "Channel mix planning by GEO", "Ongoing partner performance review"],
  },
  {
    icon: Database,
    title: "Data Led Audience Strategy",
    body: "We use owned, licensed, partner and customer data where rights and permissions exist. Our approach is built around quality, legitimacy and responsible use — not volume claims. Audience strategy is documented, repeatable and aligned with destination-market rules.",
    points: ["Audience segmentation and modelling", "GEO and channel planning", "Consent and rights documentation", "Permission and suppression records"],
  },
  {
    icon: Send,
    title: "Email and SMS Campaign Delivery",
    body: "We plan and deliver email and SMS campaigns with clear sender identification, working opt-out, suppression handling, performance tracking and complaint review. We do not claim guaranteed inbox placement and do not engage in unsolicited messaging.",
    points: ["Campaign planning and segmentation", "Sender control and opt-out", "Suppression and complaints handling", "Performance monitoring and reporting"],
  },
  {
    icon: Target,
    title: "Lead Generation and Customer Acquisition",
    body: "Acquisition funnels, partner campaigns, audience development, tracking and qualification — built for measurable conversion. Designed so operators can see where users come from, how they convert and where to invest next.",
    points: ["Funnel design and optimisation", "Tracking and attribution setup", "Audience qualification and scoring", "Conversion measurement and review"],
  },
  {
    icon: Cpu,
    title: "Marketing Technology and AI Optimisation",
    body: "Campaign intelligence, audience segmentation, optimisation signals, reporting and data enrichment — applied to find the next best action, not to replace operator judgement. We focus on signal quality and reproducible results.",
    points: ["Optimisation signals and scoring", "Audience modelling and look-alikes", "Reporting and BI integration", "Lawful data enrichment"],
  },
  {
    icon: Briefcase,
    title: "Strategic Consulting",
    body: "Senior commercial support for acquisition strategy, GEO planning, partner selection, performance review and market entry. Engagements are direct, structured and outcome-led.",
    points: ["Market entry and GEO playbooks", "Partner mix and selection", "Programme review and recovery", "C-level acquisition strategy"],
  },
];

export default function Services() {
  React.useEffect(() => {
    document.title = "Services | PlayerReach";
  }, []);
  return (
    <div data-testid="services-page">
      <PageHeader
        eyebrow="Services"
        title="An end-to-end acquisition stack for regulated entertainment brands."
        subtitle="From partner sourcing to AI optimisation, PlayerReach delivers acquisition systems that operators, platforms and entertainment groups can scale with confidence."
      />

      <section className="section-pad">
        <div className="container-pr space-y-10">
          {SERVICES.map((s, i) => (
            <div key={s.title} className="card-flat p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid={`service-detail-${i}`}>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0052FF]/10 text-[#0052FF] border border-[#0052FF]/20">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs text-slate-400 font-mono-pr">0{i + 1} / 06</span>
                </div>
                <h3 className="font-display mt-5 text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">{s.title}</h3>
              </div>
              <div className="lg:col-span-7">
                <p className="text-slate-600 leading-relaxed">{s.body}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, dark = true }) {
  return (
    <section className={`relative overflow-hidden ${dark ? "bg-[#0A0E1A] text-white" : "bg-white"}`}>
      <div className={`absolute inset-0 ${dark ? "grid-bg" : "grid-bg-light"} opacity-50 pointer-events-none`} />
      {dark && <div aria-hidden className="absolute inset-0 hero-beam pointer-events-none" />}
      {dark && <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />}
      <div className="relative container-pr py-24 lg:py-32">
        <p className={`text-[11px] uppercase tracking-[0.24em] font-semibold ${dark ? "text-cyan-300" : "text-cyan-700"}`}>§ {eyebrow}</p>
        <h1 className={`font-display mt-6 text-[44px] sm:text-5xl lg:text-7xl font-medium tracking-[-0.035em] leading-[1.02] max-w-5xl ${dark ? "" : "text-slate-900"}`}>{title}</h1>
        {subtitle && (
          <p className={`mt-7 text-base sm:text-lg lg:text-xl max-w-3xl leading-relaxed ${dark ? "text-slate-300/90" : "text-slate-600"}`}>{subtitle}</p>
        )}
      </div>
    </section>
  );
}

export function CTABand() {
  return (
    <section className="pb-24 lg:pb-32 bg-white">
      <div className="container-pr">
        <div className="relative overflow-hidden rounded-3xl bg-[#0A0E1A] text-white p-10 lg:p-14 border border-white/5">
          <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-10 w-[400px] h-[400px] rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-[-0.03em] leading-[1.04]">
                Ready to talk <span className="font-serif-edit italic font-normal text-cyan-300">acquisition?</span>
              </h2>
              <p className="mt-4 text-slate-300/90 max-w-2xl leading-relaxed">Tell us about your brand, your markets and your current acquisition challenge. We will respond with a focused, commercially grounded next step.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/contact" className="btn-primary" data-testid="cta-band-start">
                Start a Conversation <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
